import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

// Safety net around the map: if anything in the map stack throws during
// render, the rest of the UI (search, filters, About, sign-in) keeps working
// instead of the whole route blanking behind a full-page crash overlay.
export default class MapErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[MapErrorBoundary] map crashed:', error, info.componentStack)
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.error) {
      return (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-[#0A0A0B] p-6">
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#141416]/95 p-6 text-center shadow-2xl">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/10">
              <AlertTriangle size={22} className="text-amber-400" />
            </div>
            <h2 className="mt-4 text-lg font-bold tracking-tight text-white">
              The map couldn't load
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-white/60">
              Something went wrong while starting the map engine. This can happen if the
              browser blocks graphics (WebGL) — in Brave, try setting Shields to
              "Standard" for this site, then reload.
            </p>
            <button
              onClick={this.handleReload}
              className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-black hover:bg-amber-300 transition-colors"
            >
              <RefreshCw size={16} />
              Reload page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
